import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TextStyle, TouchableOpacity, ViewStyle } from 'react-native';
import Animated, {
    SharedValue,
    interpolate,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { AlertTriangleWhiteSmall } from '@/assets/icons/alertTriangleWhiteSmall';
import { ArrowDownThin } from '@/assets/icons/arrowDownThin';
import InfoIcon from '@/assets/icons/info-small';
import { PromoTagOutlineIcon } from '@/assets/icons/promo-tag-outline';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { designSystem, withOpacity } from '@/styles/styles';

export type PromoStatus = 'available' | 'applied' | 'in-use';

export type PromoData = {
    id: string;
    name: string;
    label?: string;
    description: string;
    error?: { shortDescription: string };
    onInfoPress?: () => void;
};

export type PromoWidgetItem = PromoData & {
    status: PromoStatus;
};

export const PROMO_WIDGET_HEIGHT = 80;
export const PROMO_WIDGET_VISIBLE_HEIGHT = 40;
export const PROMO_WIDGET_OVERLAP = -(PROMO_WIDGET_HEIGHT - PROMO_WIDGET_VISIBLE_HEIGHT);
const PROMO_CARD_MARGIN_WHEN_COLLAPSED = 6;
const ANIMATION_DURATION = 250;

export type PromoWidgetProps = {
    promos: PromoWidgetItem[];
    onSelectPromo: (promoId: string) => void;
    onExpand?: () => void;
    testID?: string;
};

export interface PromoWidgetRef {
    collapsePromos: (onCollapsed?: () => void) => void;
    arePromosExpanded: () => boolean;
}

export const PromoWidget = forwardRef<PromoWidgetRef, PromoWidgetProps>((props, ref) => {
    const { promos, onSelectPromo, onExpand, testID } = props;
    const { t } = useTranslation('common');
    const promosExpandedValue = useSharedValue(0);
    const contentHeight = useSharedValue(0);

    useImperativeHandle(ref, () => ({
        collapsePromos: (onCollapsed?: () => void) => {
            collapsePromoWidget(onCollapsed);
        },
        arePromosExpanded: () => promosExpandedValue.value > 0,
    }));

    const appliedPromo = useMemo(() => promos.find(p => p.status === 'applied'), [promos]);

    const titleText = useMemo(() => {
        if (appliedPromo) {
            return t('promo_applied', { name: appliedPromo.name });
        }
        if (promos.length === 1) {
            return t('promo_available', { name: promos[0].name });
        }
        return t('promos_available_count', { count: promos.length });
    }, [appliedPromo, promos, t]);

    const onContentLayout = (event: { nativeEvent: { layout: { height: number } } }) => {
        contentHeight.value = event.nativeEvent.layout.height + PROMO_WIDGET_HEIGHT;
    };

    const animatedStyle = useAnimatedStyle(() => {
        const height = interpolate(promosExpandedValue.value, [0, 1], [PROMO_WIDGET_HEIGHT, contentHeight.value]);
        const marginHorizontal = interpolate(promosExpandedValue.value, [0, 1], [PROMO_CARD_MARGIN_WHEN_COLLAPSED, 0]);
        return { height, marginHorizontal };
    });

    const iconStyle = useAnimatedStyle(() => {
        const rotateDeg = interpolate(promosExpandedValue.value, [0, 1], [0, 180]);
        return { transform: [{ rotate: `${rotateDeg}deg` }] };
    });

    const collapsePromoWidget = (onFinished?: () => void) => {
        promosExpandedValue.value = withTiming(0, { duration: ANIMATION_DURATION }, finished => {
            if (finished && onFinished) {
                runOnJS(onFinished)();
            }
        });
    };

    const toggleExpand = () => {
        if (promosExpandedValue.value === 0) {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.EXPAND_AVAILABLE_PROMOS_FROM_CHECKOUT);
            onExpand?.();
            promosExpandedValue.value = withTiming(1, { duration: ANIMATION_DURATION });
        } else {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.COLLAPSE_AVAILABLE_PROMOS_FROM_CHECKOUT);
            collapsePromoWidget();
        }
    };

    const handleSelectPromo = (promoId: string) => {
        onSelectPromo(promoId);
        collapsePromoWidget();
    };

    return (
        <TouchableOpacity onPress={toggleExpand} activeOpacity={1} testID={testID}>
            <Animated.View style={[styles.container, animatedStyle]}>
                <Row alignItems="center" justifyContent="center">
                    <Text variant="labelLarge2" marginRight="s4" color={ds.colorToken.text.primary}>
                        {titleText}
                    </Text>
                    <Animated.View style={iconStyle}>
                        <ArrowDownThin />
                    </Animated.View>
                </Row>
                <Box marginTop="s10" onLayout={onContentLayout}>
                    {promos.map((promo, index) => {
                        const isLast = index === promos.length - 1;
                        return (
                            <PromoRow
                                key={promo.id}
                                promo={promo}
                                isLast={isLast}
                                onPress={() => handleSelectPromo(promo.id)}
                                isExpanded={promosExpandedValue}
                            />
                        );
                    })}
                </Box>
            </Animated.View>
        </TouchableOpacity>
    );
});

type PromoRowProps = {
    promo: PromoWidgetItem;
    onPress: () => void;
    isLast: boolean;
    isExpanded: SharedValue<number>;
};

const PromoRow = ({ promo, onPress, isLast, isExpanded }: PromoRowProps) => {
    const hasError = !!promo.error;

    const gapAnimatedStyle = useAnimatedStyle(() => {
        const marginLeft = interpolate(isExpanded.value, [0, 1], [0, PROMO_CARD_MARGIN_WHEN_COLLAPSED * 2]);
        return { marginLeft };
    });

    return (
        <Row
            gap="s16"
            paddingVertical="s16"
            alignItems="center"
            borderTopWidth={1}
            marginBottom={isLast ? 's4' : 's0'}
            style={styles.promotionRow}
        >
            <PromoTagOutlineIcon />
            <Box flex={1}>
                <Row alignItems="center">
                    <Text variant="bodyLarge" lineHeight={24} letterSpacing={-0.43}>
                        {promo.label ?? promo.name}
                    </Text>
                    {promo.onInfoPress ? (
                        <TouchableOpacity
                            onPress={promo.onInfoPress}
                            style={styles.infoIconContainer}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                            <InfoIcon
                                width={20}
                                height={20}
                                color={ds.colorValue.icon.primary}
                                fillColor={ds.colorValue.icon.primary}
                            />
                        </TouchableOpacity>
                    ) : null}
                </Row>
                <Text variant="bodyMedium" style={styles.promoDescriptionText}>
                    {hasError ? promo.error?.shortDescription : promo.description}
                </Text>
            </Box>
            <Animated.View style={gapAnimatedStyle}>
                {hasError ? (
                    <Box padding="s10" borderRadius="r10" style={styles.errorIconContainer}>
                        <AlertTriangleWhiteSmall />
                    </Box>
                ) : (
                    <PromoActionButton status={promo.status} onPress={onPress} promoId={promo.id} />
                )}
            </Animated.View>
        </Row>
    );
};

type PromoActionButtonProps = {
    status: PromoStatus;
    onPress: () => void;
    promoId: string;
};

const PromoActionButton = ({ status, onPress, promoId }: PromoActionButtonProps) => {
    const { t } = useTranslation('common');
    const config = PROMO_BUTTON_CONFIG[status];

    return (
        <Button
            onPress={onPress}
            size="s"
            hierarchy="secondary"
            disabled={config.disabled}
            label={t(config.labelKey)}
            textStyle={config.textStyle}
            style={config.style}
            testID={`apply-promo-button-${promoId}`}
        />
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: ds.colorValue.background.brand.strong,
        borderRadius: 16,
        borderCurve: 'continuous',
        paddingHorizontal: 16,
        paddingVertical: 10,
        overflow: 'hidden',
    },
    applyButton: {
        paddingHorizontal: 12,
        height: 36,
        backgroundColor: ds.colorValue.background.transparent.strong,
    },
    applyText: {
        color: ds.colorValue.text.primary,
    },
    appliedButton: {
        paddingHorizontal: 12,
        height: 36,
        backgroundColor: ds.colorValue.background.neutral.inverse,
    },
    appliedText: {
        color: ds.colorValue.text.brandoninverse,
    },
    inUseButton: {
        paddingHorizontal: 12,
        height: 36,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
    inUseText: {
        color: withOpacity(ds.colorValue.text.primary, 0.5),
    },
    promoDescriptionText: {
        color: withOpacity(ds.colorValue.text.primary, 0.5),
        marginTop: 1,
    },
    promotionRow: {
        borderTopColor: ds.colorValue.border.strong,
    },
    errorIconContainer: {
        backgroundColor: withOpacity(designSystem.colors.neutralBlack, 0.2),
    },
    infoIconContainer: {
        opacity: 0.5,
        marginLeft: 4,
    },
});

type PromoButtonConfig = {
    labelKey: 'promo_applied_action' | 'promo_in_use' | 'apply';
    textStyle: TextStyle;
    style: ViewStyle;
    disabled?: boolean;
};

const PROMO_BUTTON_CONFIG: Record<PromoStatus, PromoButtonConfig> = {
    applied: { labelKey: 'promo_applied_action', textStyle: styles.appliedText, style: styles.appliedButton },
    'in-use': { labelKey: 'promo_in_use', textStyle: styles.inUseText, style: styles.inUseButton, disabled: true },
    available: { labelKey: 'apply', textStyle: styles.applyText, style: styles.applyButton },
};
